import React from 'react';
import { motion } from 'framer-motion';
export interface BarModelSegmentValue {
  /** Value of this segment (e.g. 20). Optional — when omitted, segment shows nothing. */
  value?: number | string;
  /** Custom label override (e.g. "?", "x") */
  label?: string;
}
export interface BarModelProps {
  /** The total value the whole bar represents (e.g. 80) */
  total: number | string;
  /** Optional total label (overrides display of `total`) */
  totalLabel?: string;
  /** Optional unit displayed with values (e.g. "€", "m", "kg") */
  unit?: string;
  /** Number of equal segments the bar is divided into */
  segments: number;
  /** 0-based indices of segments that are highlighted */
  highlighted?: number[];
  /** Show "?" above the highlighted region instead of a value */
  showUnknownMarker?: boolean;
  /** Show per-segment values (when each is the same: total / segments) */
  showSegmentValues?: boolean;
  /** Optional explicit per-segment values/labels (overrides equal split) */
  segmentValues?: BarModelSegmentValue[];
  /** Optional label below the bar (e.g. "80 leerlingen") */
  caption?: string;
  /** Layout size */
  size?: 'sm' | 'md' | 'lg';
  /** Display label (header) */
  label?: string;
}
const SIZE_MAP = {
  sm: {
    height: 'h-10',
    text: 'text-xs',
    total: 'text-base',
    gap: 'gap-0.5'
  },
  md: {
    height: 'h-14',
    text: 'text-sm',
    total: 'text-xl',
    gap: 'gap-1'
  },
  lg: {
    height: 'h-20',
    text: 'text-base',
    total: 'text-2xl',
    gap: 'gap-1.5'
  }
} as const;
const formatValue = (v: number | string | undefined, unit?: string): string => {
  if (v === undefined) return '';
  if (typeof v === 'number') {
    const formatted = Number.isInteger(v) ? v.toLocaleString('nl-NL') : v.toLocaleString('nl-NL', {
      maximumFractionDigits: 2
    });
    return unit ? `${unit}${formatted}` : formatted;
  }
  return unit ? `${unit}${v}` : v;
};
/**
 * BarModel — Strookmodel / Singapore bar model
 *
 * Canonical visual for "deel van een geheel" reasoning:
 * - Fractions of a quantity (¾ van 24)
 * - Percentages of a quantity (25% van 80)
 * - Ratio splits (verdeel 60 in 2:3)
 * - Multi-step word problems
 *
 * Designed schoolbook-style: clean rounded segments, indigo highlight on
 * the focus region, total brace above the whole, optional per-segment
 * values below.
 */
export const BarModel: React.FC<BarModelProps> = ({
  total,
  totalLabel,
  unit,
  segments,
  highlighted = [],
  showUnknownMarker = false,
  showSegmentValues = false,
  segmentValues,
  caption,
  size = 'md',
  label
}) => {
  const safeSegments = Math.max(1, Math.min(20, segments));
  const sizing = SIZE_MAP[size];
  const highlightSet = new Set(highlighted);
  const numericTotal = typeof total === 'number' ? total : Number(total);
  const equalSplit = Number.isFinite(numericTotal) && safeSegments > 0 ? numericTotal / safeSegments : null;
  const totalDisplay = totalLabel ?? formatValue(total, unit);
  // Compute highlighted-region value when total is numeric and split is even
  const highlightedCount = highlighted.length;
  const highlightedValue = equalSplit !== null && highlightedCount > 0 ? equalSplit * highlightedCount : null;
  // Calculate the percent span of the highlighted region (assumes contiguous)
  const sortedHighlighted = [...highlighted].sort((a, b) => a - b);
  const isContiguous = sortedHighlighted.length > 0 && sortedHighlighted.every((v, i) => i === 0 || v === sortedHighlighted[i - 1] + 1);
  return <div className="flex flex-col items-center w-full max-w-2xl mx-auto" aria-label={`Strookmodel, totaal ${totalDisplay}`} data-id="element-13074" data-mp="2tas1727p73yp">
      
      {label && <div className="text-sm font-bold text-slate-700 mb-3 text-center" data-id="element-13075" data-mp="37159j5oi4kok">
          {label}
        </div>}

      {/* Total brace + label above the bar */}
      <div className="w-full px-2 mb-2" data-id="element-13076" data-mp="3gzb8bef0hap7">
        <div className="flex flex-col items-center" data-id="element-13077" data-mp="3h0cizhs47v6e">
          <div className={`font-black text-slate-800 tabular-nums ${sizing.total} mb-1`} data-id="element-13078" data-mp="3c19qsxx8hjfu">
            
            {totalDisplay}
          </div>
          <div className="w-full h-4 relative" data-id="element-13079" data-mp="19djhmeswbd8r">
            <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 100 20" data-id="element-13080" data-mp="1ecw6f53buudz">
              
              <path d="M 0 20 Q 0 10 5 10 L 45 10 Q 50 10 50 0 Q 50 10 55 10 L 95 10 Q 100 10 100 20" fill="none" stroke="#94a3b8" strokeWidth="2" vectorEffect="non-scaling-stroke" data-id="element-13081" data-mp="1j4xcuadtdqw3" />
              
            </svg>
          </div>
        </div>
      </div>

      {/* The segmented bar */}
      <div className={`flex ${sizing.gap} w-full px-2 relative`} role="img" aria-label={`Verdeeld in ${safeSegments} gelijke stukken`} data-id="element-13082" data-mp="vdi707mtwrg3">
        
        {Array.from({
        length: safeSegments
      }).map((_, i) => {
        const isHi = highlightSet.has(i);
        const isFirst = i === 0;
        const isLast = i === safeSegments - 1;
        return <motion.div key={i} initial={{
          opacity: 0,
          scaleX: 0.85
        }} animate={{
          opacity: 1,
          scaleX: 1
        }} transition={{
          delay: i * 0.04,
          duration: 0.25
        }} className={`flex-1 ${sizing.height} flex items-center justify-center border-2 ${isHi ? 'bg-indigo-500 border-indigo-600 text-white shadow-sm' : 'bg-slate-50 border-slate-200 text-slate-600'} ${isFirst ? 'rounded-l-lg' : ''} ${isLast ? 'rounded-r-lg' : ''} ${!isFirst && !isLast ? '' : ''}`} style={{
          originX: 0.5
        }} data-id="element-13083" data-mp="2bunuuuikhcxh" data-mpi={i}>
              
              {segmentValues && segmentValues[i] ? <span className={`font-bold ${sizing.text}`} data-id="element-13084" data-mp="3dkclabogbsit">
                  {segmentValues[i].label ?? formatValue(segmentValues[i].value, unit)}
                </span> : showSegmentValues && equalSplit !== null ? <span className={`font-bold ${sizing.text}`} data-id="element-13085" data-mp="33m6kxyiamug2">
                  {formatValue(equalSplit, unit)}
                </span> : null}
            </motion.div>;
      })}
      </div>

      {/* Highlighted-region indicator below the bar */}
      {highlightedCount > 0 && isContiguous && <div className="w-full px-2 mt-1" data-id="element-13086" data-mp="11sqpmob9grer">
          <div className="flex" aria-hidden data-id="element-13087" data-mp="2w9qukkrrwm1z">
            {/* spacer to start of highlighted region */}
            <div style={{
          flex: `${sortedHighlighted[0]} 0 0`
        }} data-id="element-13088" data-mp="312jwl0vuxgad" />
          
            <div style={{
          flex: `${highlightedCount} 0 0`
        }} className="flex flex-col items-center" data-id="element-13089" data-mp="2mdngqhnk6yz7">
            
              <div className="w-full h-3 flex items-start" data-id="element-13090" data-mp="2rbyv95ostxnr">
                <div className="w-px h-2 bg-indigo-500" data-id="element-13091" data-mp="3f2b90x6q6om9" />
                <div className="flex-1 border-b-2 border-indigo-500" data-id="element-13092" data-mp="3k0cqxesf0hw5" />
                <div className="w-px h-2 bg-indigo-500" data-id="element-13093" data-mp="3jzl5uxwg22k4" />
              </div>
              <div className="mt-1 inline-flex items-center px-3 py-1 rounded-lg bg-indigo-50 border border-indigo-200" data-id="element-13094" data-mp="352alez1tpmc9">
                <span className="text-sm font-black text-indigo-700 tabular-nums" data-id="element-13095" data-mp="3a0c4vl35vno9">
                  {showUnknownMarker ? '?' : highlightedValue !== null ? formatValue(highlightedValue, unit) : '?'}
                </span>
              </div>
            </div>
            {/* spacer after highlighted region */}
            <div style={{
          flex: `${safeSegments - sortedHighlighted[0] - highlightedCount} 0 0`
        }} data-id="element-13096" data-mp="grhnyls1x6jv" />
          
          </div>
        </div>}

      {caption && <div className="mt-4 text-xs font-medium text-slate-500 text-center" data-id="element-13097" data-mp="pos2qh42vdtj">
          {caption}
        </div>}
    </div>;
};